/**
 * Time Tracking Redux Slice
 * Comprehensive time tracking state management with timer support
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import timeTrackingService from '../entities/timeTracking/api/timeTrackingService';
import {
  ActiveTimer,
  TimeTrackingState,
  CreateTimeLogDTO,
  UpdateTimeLogDTO,
  StartTimerDTO,
  StopTimerDTO,
  TimeTrackingFilters,
  calculateElapsedSeconds,
} from '../entities/timeTracking/model/types';

const initialState: TimeTrackingState = {
  logs: [],
  activeTimer: null,
  summary: null,
  currentReport: null,
  loading: false,
  error: null,
  filters: {},
};

// Async Thunks

/**
 * Fetch time logs with filters
 */
export const fetchTimeLogs = createAsyncThunk(
  'timeTracking/fetchTimeLogs',
  async (filters: TimeTrackingFilters | undefined, { rejectWithValue }) => {
    try {
      return await timeTrackingService.getTimeLogs(filters);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch time logs');
    }
  }
);

/**
 * Fetch time logs for a specific task
 */
export const fetchTaskTimeLogs = createAsyncThunk(
  'timeTracking/fetchTaskTimeLogs',
  async (taskId: string, { rejectWithValue }) => {
    try {
      return await timeTrackingService.getTaskTimeLogs(taskId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch task time logs');
    }
  }
);

/**
 * Create a new time log (manual entry)
 */
export const createTimeLog = createAsyncThunk(
  'timeTracking/createTimeLog',
  async (data: CreateTimeLogDTO, { rejectWithValue }) => {
    try {
      return await timeTrackingService.createTimeLog(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create time log');
    }
  }
);

/**
 * Update an existing time log
 */
export const updateTimeLog = createAsyncThunk(
  'timeTracking/updateTimeLog',
  async ({ logId, updates }: { logId: string; updates: UpdateTimeLogDTO }, { rejectWithValue }) => {
    try {
      return await timeTrackingService.updateTimeLog(logId, updates);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update time log');
    }
  }
);

/**
 * Delete a time log
 */
export const deleteTimeLog = createAsyncThunk(
  'timeTracking/deleteTimeLog',
  async (logId: string, { rejectWithValue }) => {
    try {
      await timeTrackingService.deleteTimeLog(logId);
      return logId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete time log');
    }
  }
);

/**
 * Start a timer for a task
 */
export const startTimer = createAsyncThunk(
  'timeTracking/startTimer',
  async (data: StartTimerDTO & { taskName: string }, { rejectWithValue }) => {
    try {
      const log = await timeTrackingService.startTimer(data);

      // Store timer in localStorage for persistence
      const timerData: ActiveTimer = {
        id: log.id,
        taskId: data.taskId,
        taskName: data.taskName,
        startTime: log.startTime || new Date().toISOString(),
        elapsedSeconds: 0,
        isPaused: false,
        notes: data.notes,
      };
      localStorage.setItem('activeTimer', JSON.stringify(timerData));

      return timerData;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start timer');
    }
  }
);

/**
 * Stop the active timer
 */
export const stopTimer = createAsyncThunk(
  'timeTracking/stopTimer',
  async (data: StopTimerDTO | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const activeTimer = state.timeTracking.activeTimer as ActiveTimer;

      if (!activeTimer || !activeTimer.id) {
        throw new Error('No active timer found');
      }

      const log = await timeTrackingService.stopTimer(activeTimer.id, data);

      // Clear timer from localStorage
      localStorage.removeItem('activeTimer');

      return log;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to stop timer');
    }
  }
);

/**
 * Pause the active timer
 */
export const pauseTimer = createAsyncThunk(
  'timeTracking/pauseTimer',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const activeTimer = state.timeTracking.activeTimer as ActiveTimer;

      if (!activeTimer || !activeTimer.id) {
        throw new Error('No active timer found');
      }

      await timeTrackingService.pauseTimer(activeTimer.id);

      // Update localStorage
      const updatedTimer = {
        ...activeTimer,
        isPaused: true,
        pausedAt: new Date().toISOString(),
      };
      localStorage.setItem('activeTimer', JSON.stringify(updatedTimer));

      return updatedTimer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to pause timer');
    }
  }
);

/**
 * Resume a paused timer
 */
export const resumeTimer = createAsyncThunk(
  'timeTracking/resumeTimer',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const activeTimer = state.timeTracking.activeTimer as ActiveTimer;

      if (!activeTimer || !activeTimer.id) {
        throw new Error('No active timer found');
      }

      await timeTrackingService.resumeTimer(activeTimer.id);

      // Update localStorage
      const updatedTimer = {
        ...activeTimer,
        isPaused: false,
        pausedAt: undefined,
      };
      localStorage.setItem('activeTimer', JSON.stringify(updatedTimer));

      return updatedTimer;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to resume timer');
    }
  }
);

/**
 * Restore active timer from localStorage (on app load)
 */
export const restoreActiveTimer = createAsyncThunk(
  'timeTracking/restoreActiveTimer',
  async (_, { rejectWithValue }) => {
    try {
      const stored = localStorage.getItem('activeTimer');
      if (!stored) {
        return null;
      }

      const timerData = JSON.parse(stored) as ActiveTimer;

      // Verify timer is still active on backend
      const activeLog = await timeTrackingService.getActiveTimer();

      if (!activeLog || activeLog.id !== timerData.id) {
        // Timer is no longer active on backend, clear local storage
        localStorage.removeItem('activeTimer');
        return null;
      }

      // Calculate elapsed time
      timerData.elapsedSeconds = calculateElapsedSeconds(timerData.startTime);

      return timerData;
    } catch (error: any) {
      localStorage.removeItem('activeTimer');
      return null;
    }
  }
);

/**
 * Fetch time summary
 */
export const fetchTimeSummary = createAsyncThunk(
  'timeTracking/fetchTimeSummary',
  async ({ boardId, filters }: { boardId: string; filters?: TimeTrackingFilters }, { rejectWithValue }) => {
    try {
      return await timeTrackingService.getTimeSummary(boardId, filters);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch time summary');
    }
  }
);

/**
 * Generate time report
 */
export const generateTimeReport = createAsyncThunk(
  'timeTracking/generateTimeReport',
  async (
    {
      boardId,
      period,
      startDate,
      endDate,
    }: {
      boardId: string;
      period: 'day' | 'week' | 'sprint' | 'month' | 'custom';
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await timeTrackingService.generateReport(boardId, period, startDate, endDate);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to generate report');
    }
  }
);

// Slice
const timeTrackingSlice = createSlice({
  name: 'timeTracking',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<TimeTrackingFilters>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    updateTimerElapsed: (state, action: PayloadAction<number>) => {
      if (state.activeTimer) {
        state.activeTimer.elapsedSeconds = action.payload;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    clearTimeLogs: (state) => {
      state.logs = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch time logs
    builder
      .addCase(fetchTimeLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload;
      })
      .addCase(fetchTimeLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch task time logs
    builder
      .addCase(fetchTaskTimeLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaskTimeLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.logs = action.payload;
      })
      .addCase(fetchTaskTimeLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create time log
    builder
      .addCase(createTimeLog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTimeLog.fulfilled, (state, action) => {
        state.loading = false;
        state.logs.unshift(action.payload);
      })
      .addCase(createTimeLog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update time log
    builder
      .addCase(updateTimeLog.fulfilled, (state, action) => {
        const index = state.logs.findIndex((log) => log.id === action.payload.id);
        if (index !== -1) {
          state.logs[index] = action.payload;
        }
      })
      .addCase(updateTimeLog.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete time log
    builder
      .addCase(deleteTimeLog.fulfilled, (state, action) => {
        state.logs = state.logs.filter((log) => log.id !== action.payload);
      })
      .addCase(deleteTimeLog.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Start timer
    builder
      .addCase(startTimer.fulfilled, (state, action) => {
        state.activeTimer = action.payload;
      })
      .addCase(startTimer.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Stop timer
    builder
      .addCase(stopTimer.fulfilled, (state, action) => {
        state.activeTimer = null;
        state.logs.unshift(action.payload);
      })
      .addCase(stopTimer.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Pause timer
    builder.addCase(pauseTimer.fulfilled, (state, action) => {
      state.activeTimer = action.payload;
    });

    // Resume timer
    builder.addCase(resumeTimer.fulfilled, (state, action) => {
      state.activeTimer = action.payload;
    });

    // Restore active timer
    builder.addCase(restoreActiveTimer.fulfilled, (state, action) => {
      state.activeTimer = action.payload;
    });

    // Fetch time summary
    builder
      .addCase(fetchTimeSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTimeSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.summary = action.payload;
      })
      .addCase(fetchTimeSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Generate time report
    builder
      .addCase(generateTimeReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateTimeReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload;
      })
      .addCase(generateTimeReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, updateTimerElapsed, clearError, clearTimeLogs } =
  timeTrackingSlice.actions;

export default timeTrackingSlice.reducer;
